import { convertNumberToStringForObject } from 'src/helpers/common.utils';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { PAGINATION_CONFIGURATION } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { PaginationResponseDTO, ResponseDTO } from 'src/dto/base.dto';
import {
	CheckGuarantorInvitation,
	ConfirmGuarantorInvitation,
	GetGuaranteeListDTO,
	GetGuarantorDealInfo,
	GuarantorInvitationAction
} from 'src/dto/guarantor.dto';
import { IGuaranteeApplication, IGuaranteeItem } from 'src/interfaces/guarantee/guarantee';
import { IGuarantorDetail, IGuarantorLoanDetails } from 'src/interfaces/guarantor';
import { HttpService } from 'src/services/http.service';

export interface IGuarantorStore {
	dealData: IGuarantorDetail;
	dispose(): void;
	checkGuarantorInvitation(deal: string, email: string): Promise<boolean>;
	getMortgageDeal(id: string): Promise<boolean>;
	confirmGuarantor(id: string, email: string): Promise<boolean>;
	consent(): Promise<boolean>;
	reject(): Promise<boolean>;
	totalPages: number;
	totalRecords: number;
	pageSize: number;
	pageNumber: number;

	guaranteeList: IGuaranteeItem[];

	getGuaranteeList(filter: IGuaranteeApplication): Promise<void>;
}

export class GuarantorStore implements IGuarantorStore {
	dealData: IGuarantorDetail = {
		mortgageId: ''
	};

	guaranteeList: IGuaranteeItem[] = [];
	totalPages = 0;
	totalRecords = 0;
	pageSize = 0;
	pageNumber = 0;

	constructor(private readonly http: HttpService) {
		makeAutoObservable(this, {
			dealData: observable.deep
		});
	}
	public async checkGuarantorInvitation(deal: string, email: string): Promise<boolean> {
		const dto = new CheckGuarantorInvitation({
			mortgageId: deal,
			email
		});
		const rs: ResponseDTO<boolean> = await this.http.request<CheckGuarantorInvitation, boolean>(dto);
		return rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS && !!rs.data;
	}

	public async getMortgageDeal(id: string): Promise<boolean> {
		const dto = new GetGuarantorDealInfo({ mortgageId: id });
		const rs: ResponseDTO<IGuarantorDetail> = await this.http.request<GetGuarantorDealInfo, IGuarantorDetail>(dto);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS && rs.data) {
			runInAction(() => {
				this.dealData = {
					...rs.data,
					loanDetails: {
						...convertNumberToStringForObject<IGuarantorLoanDetails>(rs.data?.loanDetails || {}, ['borrowAmount'])
					},
					mortgageId: rs.data ? rs.data.mortgageId : ''
				};
			});
			return true;
		}

		return false;
	}

	public async consent(): Promise<boolean> {
		const dto = new GuarantorInvitationAction(
			{ mortgageId: this.dealData.mortgageId },
			ENDPOINT.GUARANTOR_CONSENT_INVITATION
		);
		const rs: ResponseDTO<boolean> = await this.http.request<GuarantorInvitationAction, boolean>(dto);
		return rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS && !!rs.data;
	}
	public async reject(): Promise<boolean> {
		const dto = new GuarantorInvitationAction(
			{ mortgageId: this.dealData.mortgageId },
			ENDPOINT.GUARANTOR_REJECT_INVITATION
		);
		const rs: ResponseDTO<boolean> = await this.http.request<GuarantorInvitationAction, boolean>(dto);
		return rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS && !!rs.data;
	}
	public async confirmGuarantor(id: string, email: string): Promise<boolean> {
		const dto = new ConfirmGuarantorInvitation({
			mortgageId: id,
			email
		});
		const rs: ResponseDTO<boolean> = await this.http.request<ConfirmGuarantorInvitation, boolean>(dto);
		return rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS && !!rs.data;
	}

	public dispose(): void {
		this.dealData = {
			mortgageId: ''
		};
	}

	public async getGuaranteeList(filter: IGuaranteeApplication): Promise<void> {
		const dto = new GetGuaranteeListDTO(filter);

		const rs: PaginationResponseDTO<IGuaranteeItem[]> = await this.http.request<GetGuaranteeListDTO, IGuaranteeItem[]>(
			dto
		);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.guaranteeList = rs.data || [];
				this.totalRecords = rs.totalRecords || 0;
				this.totalPages = rs.totalPages || 0;
				this.pageSize = rs.pageSize || PAGINATION_CONFIGURATION.DEFAULT_PAGE_SIZE;
				this.pageNumber = rs.pageNumber || PAGINATION_CONFIGURATION.DEFAULT_PAGE;
			});
		}
	}
}
